<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  INTERNAL_CHAT_PERMISSIONS,
  toggleInternalChatPanel,
} from './sidepanelUi';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { checkPermissions } = usePolicy();

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const unreadCount = useMapGetter('internalChat/getUnreadCount');

const isInternalChatRoute = computed(() =>
  String(route.name || '').startsWith('internal_chat')
);

const canShow = computed(
  () =>
    isFeatureEnabledonAccount.value(
      currentAccountId.value,
      FEATURE_FLAGS.INTERNAL_CHAT
    ) &&
    checkPermissions(INTERNAL_CHAT_PERMISSIONS) &&
    !isInternalChatRoute.value
);

const isOpen = computed(() => !!uiSettings.value?.is_internal_chat_panel_open);

const hasUnreadMention = computed(() => {
  const channels = store.getters['internalChat/getChannels'] || [];
  return channels.some(channel => channel.has_unread_mention);
});

const hasAttention = computed(
  () => hasUnreadMention.value || (unreadCount.value || 0) > 0
);

const badgeCount = computed(() => {
  const count = Number(unreadCount.value || 0);
  if (count > 0) return count;
  return hasUnreadMention.value ? 1 : 0;
});

const badgeLabel = computed(() => {
  if (badgeCount.value <= 0) return '';
  return badgeCount.value > 99 ? '99+' : String(badgeCount.value);
});

const buttonLabel = computed(() => {
  const name = t('CONVERSATION.SIDEBAR.INTERNAL_CHAT');
  if (!badgeLabel.value) return name;
  return t('INTERNAL_CHAT.UNREAD_ARIA', {
    name,
    count: badgeLabel.value,
  });
});

function handleClick() {
  toggleInternalChatPanel(updateUISettings, isOpen.value);
}
</script>

<template>
  <span
    v-if="canShow"
    data-alibsoft-internal-chat-switch
    class="relative inline-flex"
  >
    <Button
      v-tooltip.bottom="buttonLabel"
      :aria-label="buttonLabel"
      ghost
      slate
      sm
      class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:duration-75"
      :class="{
        'bg-n-alpha-2 active:shadow-sm': isOpen,
        'animate-pulse ring-2 ring-n-ruby-9/70':
          !isOpen && hasUnreadMention,
        'animate-pulse': !isOpen && hasAttention && !hasUnreadMention,
      }"
      icon="i-lucide-messages-square"
      @click="handleClick"
    />
    <span
      v-if="badgeLabel"
      class="pointer-events-none absolute -right-0.5 -top-0.5 inline-grid size-4 place-items-center rounded-full bg-n-ruby-9 text-[10px] font-semibold leading-none text-white"
      :class="{ 'w-auto min-w-4 px-1': badgeLabel.length > 1 }"
    >
      {{ badgeLabel }}
    </span>
  </span>
</template>
